const EVENT_HISTORY_COUNT = 4;
const NEW_EVENT_TIME_DIFFERENCE_THRESHOLD = 300;
// 60fps
const STANDARD_ANIMATION_FRAME = 1000 / 60;

enum WheelEventSummaryPropName {
    deltaY = 'deltaY',
    deltaX = 'deltaX',
    weightedDeltaY = 'weightedDeltaY',
    weightedDeltaX = 'weightedDeltaX',
}

interface InertiaScrollDetector {
    addWheelEvent: Function;
    isInertiaScroll: Function;
}

interface WheelEventSummary {
    timeStamp: number;
    deltaX: number;
    deltaY: number;
    // In order to fairly compare two events we need to get the weighted value
    //  in case an animation frame has been skipped
    weightedDeltaX: number;
    weightedDeltaY: number;
    timeDifference: number;
}

/**
 * Determines if the next event has a larger magnitude deltaY.
 */
const isLargerMagnitudeProp = (
    prevEvent: WheelEventSummary,
    nextEvent: WheelEventSummary,
    propName: WheelEventSummaryPropName,
): boolean => Math.abs(prevEvent[propName]) < Math.abs(nextEvent[propName]);

const isInertiaScrollInDirection = (
    events: Array<WheelEventSummary>,
    propName: WheelEventSummaryPropName,
    weightedPropName: WheelEventSummaryPropName,
) => {
    const mostRecentEvent = events[events.length - 1];
    const previousEvent = events[events.length - 2];

    // If there's no delta in this direction, then it's not an inertia scroll in this direction
    if (mostRecentEvent[propName] === 0) return false;

    // If there's a chang of direction, it's a manual scroll
    if (mostRecentEvent[propName] * previousEvent[propName] < 0) return false;

    // If the next delta Y is at least two times bigger than the previous deltaY then assume it's a manual scroll.
    // There does seem to be instances where the inertia scroll has deltaY larger than the previous deltaY,
    //  my assumption is that it's when there's a missed animation frame, so it sums the two frame's deltaYs
    //  together.
    const isMuchLargerDelta =
        Math.abs(mostRecentEvent[weightedPropName]) > 3 &&
        Math.abs(previousEvent[weightedPropName]) > 3 &&
        Math.abs(mostRecentEvent[weightedPropName]) > Math.abs(previousEvent[weightedPropName]) * 2;
    if (isMuchLargerDelta) return false;

    // If the most recent event is significantly smaller than the previous event then it's not as a result
    // of inertia scroll, as that is almost always > 0.4 of the previous event.
    const ratio = mostRecentEvent[weightedPropName] / previousEvent[weightedPropName];
    const isMuchSmallerDelta =
        ratio < 0.4 && Math.abs(mostRecentEvent[weightedPropName]) > 3 && Math.abs(previousEvent[weightedPropName]) > 3;
    if (isMuchSmallerDelta) return false;

    // If the last 4 events are all greater than each other, then it's a manual scroll
    const isIncreasingScroll =
        isLargerMagnitudeProp(events[0], events[1], weightedPropName) &&
        isLargerMagnitudeProp(events[1], events[2], weightedPropName) &&
        isLargerMagnitudeProp(events[2], events[3], weightedPropName);

    if (isIncreasingScroll) return false;

    return true;
};

/**
 * The inertia scroll detector can be used to detect when a wheel event is most likely a result
 * of Apple's Magic Mouse inertia scroll, rather than a manual scroll from the user.
 *
 * Process:
 * 1. Create the inertia scroll detector:
 *    ```const inertiaScrollDetector = createInertiaScrollDetector();```
 * 2. On a wheel event add the event into the inertia scroll detector:
 *    ```inertiaScrollDetector.addWheelEvent(wheelEvent);```
 * 3. When desired, check to see if the previously added event is an inertia scroll event:
 *    ```inertiaScrollDetector.isInertiaScroll();```
 *
 * Technique:
 * We use a variety of heuristics to estimate whether an event is from inertia scroll or manual scroll
 * (as this is not made public in the event).
 * It's a manual scroll:
 *  - If the time since the last event is larger than 100ms
 *  - If there's a change in direction of the scroll
 *  - If the next event is at least two times the magnitude of the previous
 *  - If the magnitude of the event is at least 60% smaller than the previous
 *  - If the past 4 events are all increasing in magnitude
 */
export const createInertiaScrollDetector = (): InertiaScrollDetector => {
    let events: Array<WheelEventSummary> = [];

    // This is used to keep track of the "frame time" / standard time between events
    // We've seen an issue where an event would have a far shorter time between events, for some reason
    // which would make the weighted values much larger than they should be
    let continuousEventCount = 0;
    let averageContinuousEventTimeDifference = 0;

    const addWheelEvent = (event: WheelEvent) => {
        const newEvent = {
            timeStamp: event.timeStamp,
            deltaX: event.deltaX,
            deltaY: event.deltaY,
            weightedDeltaX: event.deltaX,
            weightedDeltaY: event.deltaY,
            timeDifference: event.timeStamp,
        };

        // If we have a previous event then we can perform weighting of the deltaY
        if (events.length > 1) {
            const prevEvent = events[events.length - 1];

            newEvent.timeDifference = event.timeStamp - prevEvent.timeStamp;

            // If it's a continuous event, ensure we weight its delta
            if (newEvent.timeDifference <= NEW_EVENT_TIME_DIFFERENCE_THRESHOLD) {
                continuousEventCount++;

                averageContinuousEventTimeDifference =
                    (averageContinuousEventTimeDifference * (continuousEventCount - 1) + newEvent.timeDifference) /
                    continuousEventCount;

                // If we've had less than 20 continuous events, or the new event a little bit slower than
                // the standard time between other continuous events, then weight the deltaY based on the event time
                const shouldUpdateWeightedValue =
                    continuousEventCount < 20 || newEvent.timeDifference > 1.5 * averageContinuousEventTimeDifference;

                if (shouldUpdateWeightedValue) {
                    newEvent.weightedDeltaY = Math.round(
                        event.deltaY * (STANDARD_ANIMATION_FRAME / newEvent.timeDifference),
                    );
                    newEvent.weightedDeltaX = Math.round(
                        event.deltaX * (STANDARD_ANIMATION_FRAME / newEvent.timeDifference),
                    );
                }
            }
        }

        events.push(newEvent);
        events = events.slice(-EVENT_HISTORY_COUNT);
    };

    const isInertiaScroll = (): boolean => {
        // If we don't have 4 events yet, assume it's not an inertia scroll
        if (events.length < EVENT_HISTORY_COUNT) return false;

        const mostRecentEvent = events[events.length - 1];

        // If the delay is larger than 70ms, it must be a manual scroll
        if (mostRecentEvent.timeDifference > NEW_EVENT_TIME_DIFFERENCE_THRESHOLD) return false;

        return (
            isInertiaScrollInDirection(
                events,
                WheelEventSummaryPropName.deltaY,
                WheelEventSummaryPropName.weightedDeltaY,
            ) ||
            isInertiaScrollInDirection(
                events,
                WheelEventSummaryPropName.deltaX,
                WheelEventSummaryPropName.weightedDeltaX,
            )
        );
    };

    return {
        addWheelEvent,
        isInertiaScroll,
    };
};
